import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const NAV_ITEMS = [
  { label: "Solutions", href: "#solutions", testId: "nav-solutions-link" },
  { label: "Your Privacy Rights", href: "#rights", testId: "nav-rights-link" },
  { label: "Pricing", href: "#pricing", testId: "nav-pricing-link" },
  { label: "UnTrackable", href: "#substack", testId: "nav-substack-link" },
];

export default function Navbar({ activeSection }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background/80 backdrop-blur-lg border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <span className="font-heading text-lg md:text-xl tracking-tight text-foreground">
              White Glove Privacy
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={item.testId}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  activeSection === item.href.replace("#", "")
                    ? "text-foreground bg-accent"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                }`}
              >
                {item.label}
              </a>
            ))}
            <Button
              className="ml-3 h-10 px-5 text-sm rounded-xl bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 btn-primary-hover"
              data-testid="nav-cta-button"
              onClick={(e) => handleNavClick(e, "#pricing")}
            >
              Get Started
            </Button>
          </nav>

          {/* Mobile Nav */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                data-testid="nav-mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-background p-0">
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-border">
                  <SheetTitle className="font-heading text-lg">White Glove Privacy</SheetTitle>
                  <SheetDescription className="sr-only">Navigation menu</SheetDescription>
                </div>
                <nav className="flex flex-col p-4 gap-1">
                  {NAV_ITEMS.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      data-testid={`mobile-${item.testId}`}
                      onClick={(e) => handleNavClick(e, item.href)}
                      className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        activeSection === item.href.replace("#", "")
                          ? "text-foreground bg-accent"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                      }`}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
                <div className="p-4 mt-auto border-t border-border">
                  <Button
                    className="w-full h-11 rounded-xl bg-primary text-primary-foreground"
                    onClick={(e) => handleNavClick(e, "#pricing")}
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
